import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { getAdminSection } from '../../admin/adminSections';
import {
  buildProductImageUrl,
  listAdminProducts,
} from '../../services/adminProductService';
import {
  buildVariantImageUrl,
  listAdminVariants,
} from '../../services/adminVariantService';
import {
  createAdminInventory,
  deleteAdminInventory,
  getAdminInventory,
  updateAdminInventory,
} from '../../services/adminInventoryService';

const initialFormValues = {
  product_id: '',
  variant_id: '',
  stock: '',
  reserved_stock: '',
};

function normalizeProduct(item) {
  return {
    product_id: item.product_id ?? item.id ?? item.productId ?? '',
    name: item.name ?? '',
    image: item.image ?? '',
  };
}

function normalizeVariant(item) {
  return {
    variant_id: item.variant_id ?? item.id ?? item.variantId ?? '',
    product_id: item.product_id ?? item.productId ?? '',
    variant_name: item.variant_name ?? item.variantName ?? '',
    sku: item.sku ?? '',
    image: item.image ?? '',
    price: item.price ?? '',
    weight: item.weight ?? '',
    status: String(item.status ?? '1'),
  };
}

function normalizeInventory(item) {
  if (!item) {
    return null;
  }

  return {
    inventory_id: item.inventory_id ?? item.id ?? item.inventoryId ?? '',
    variant_id: item.variant_id ?? item.variantId ?? '',
    stock: item.stock ?? '',
    reserved_stock: item.reserved_stock ?? item.reservedStock ?? '',
    updated_at: item.updated_at ?? item.updatedAt ?? '',
  };
}

export default function InventoryPage() {
  const section = getAdminSection('inventory');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [inventoryItem, setInventoryItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVariantLoading, setIsVariantLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formValues, setFormValues] = useState(initialFormValues);

  const visibleColumns = useMemo(() => {
    const fields = [
      { key: 'inventory_id', label: 'Inventory ID' },
      { key: 'variant_name', label: 'Variant Name' },
      { key: 'sku', label: 'SKU' },
      { key: 'stock', label: 'Stock' },
      { key: 'reserved_stock', label: 'Reserved' },
      { key: 'updated_at', label: 'Updated At' },
    ];

    if (isMobile) {
      return fields.slice(0, 3);
    }

    if (isTablet) {
      return fields.slice(0, 5);
    }

    return fields;
  }, [isMobile, isTablet]);

  const selectedProduct =
    products.find(
      (product) => String(product.product_id) === String(formValues.product_id)
    ) || null;

  const selectedVariant =
    variants.find(
      (variant) => String(variant.variant_id) === String(formValues.variant_id)
    ) || null;

  const inventoryRows = useMemo(() => {
    if (!inventoryItem || !selectedVariant) {
      return [];
    }

    return [
      {
        ...inventoryItem,
        variant_name: selectedVariant.variant_name,
        sku: selectedVariant.sku,
      },
    ];
  }, [inventoryItem, selectedVariant]);

  const headerMetrics = [
    {
      label: 'Products loaded',
      value: String(products.length),
    },
    {
      label: 'Variants ready',
      value: formValues.product_id ? String(variants.length) : 'Select product',
    },
    {
      label: 'Inventory status',
      value: inventoryItem ? 'Loaded' : 'Awaiting selection',
    },
  ];

  const fetchProducts = async () => {
    try {
      const response = await listAdminProducts();
      setProducts(response.items.map(normalizeProduct));
    } catch (error) {
      setSubmitError((current) => current || error);
      setProducts([]);
    }
  };

  const fetchVariants = async (productId) => {
    if (!productId) {
      setVariants([]);
      return;
    }

    setIsVariantLoading(true);

    try {
      const response = await listAdminVariants(productId);
      setVariants(response.items.map(normalizeVariant));
      setSubmitError(null);
    } catch (error) {
      setSubmitError(error);
      setVariants([]);
    } finally {
      setIsVariantLoading(false);
    }
  };

  const fetchInventory = async (variantId) => {
    if (!variantId) {
      setInventoryItem(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await getAdminInventory(variantId);
      const normalizedItem = normalizeInventory(response.item);
      setInventoryItem(normalizedItem);

      if (normalizedItem && !editingId) {
        setFormValues((current) => ({
          ...current,
          stock: String(normalizedItem.stock ?? ''),
          reserved_stock: String(normalizedItem.reserved_stock ?? ''),
        }));
      }

      setSubmitError(null);
    } catch (error) {
      setInventoryItem(null);
      setSubmitError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!formValues.product_id) {
      setVariants([]);
      setInventoryItem(null);
      setFormValues((current) => ({
        ...current,
        variant_id: '',
      }));
      return;
    }

    fetchVariants(formValues.product_id);
  }, [formValues.product_id]);

  useEffect(() => {
    if (formValues.variant_id) {
      fetchInventory(formValues.variant_id);
    } else {
      setInventoryItem(null);
      setIsLoading(false);
    }
  }, [formValues.variant_id]);

  const resetForm = () => {
    setFormValues(initialFormValues);
    setEditingId(null);
    setFieldErrors({});
    setSubmitError(null);
    setSubmitSuccess('');
    setInventoryItem(null);
  };

  const handleFieldChange = (field) => (event) => {
    setFormValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));

    setFieldErrors((current) => ({
      ...current,
      [field]: '',
    }));

    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleProductChange = (_, option) => {
    setFormValues((current) => ({
      ...current,
      product_id: option ? String(option.product_id) : '',
      variant_id: '',
      stock: '',
      reserved_stock: '',
    }));

    setEditingId(null);
    setInventoryItem(null);
    setFieldErrors((current) => ({
      ...current,
      product_id: '',
      variant_id: '',
    }));
    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleVariantChange = (_, option) => {
    setFormValues((current) => ({
      ...current,
      variant_id: option ? String(option.variant_id) : '',
      stock: '',
      reserved_stock: '',
    }));

    setEditingId(null);
    setInventoryItem(null);
    setFieldErrors((current) => ({
      ...current,
      variant_id: '',
    }));
    setSubmitError(null);
    setSubmitSuccess('');
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.product_id) {
      errors.product_id = 'Please select a product first.';
    }

    if (!formValues.variant_id) {
      errors.variant_id = 'Please select a variant.';
    }

    if (!formValues.stock.trim()) {
      errors.stock = 'Stock quantity is required.';
    }

    if (editingId && !formValues.reserved_stock.trim()) {
      errors.reserved_stock = 'Reserved stock is required while updating.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSubmitError({
        message: 'Please review the inventory form before submitting.',
      });
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    setSubmitSuccess('');
    setFieldErrors({});

    try {
      if (editingId) {
        const result = await updateAdminInventory({
          inventory_id: editingId,
          stock: formValues.stock,
          reserved_stock: formValues.reserved_stock,
        });
        setSubmitSuccess(result?.message || 'Inventory updated successfully.');
      } else {
        const result = await createAdminInventory({
          variant_id: formValues.variant_id,
          stock: formValues.stock,
        });
        setSubmitSuccess(result?.message || 'Inventory created successfully.');
      }

      await fetchInventory(formValues.variant_id);
    } catch (error) {
      setSubmitError(error);
      setFieldErrors(error.validationErrors || {});
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.inventory_id);
    setFormValues((current) => ({
      ...current,
      stock: String(item.stock ?? ''),
      reserved_stock: String(item.reserved_stock ?? ''),
    }));
    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleDelete = async (item) => {
    setDeletingId(item.inventory_id);
    setSubmitError(null);
    setSubmitSuccess('');

    try {
      const result = await deleteAdminInventory(item.inventory_id);
      setSubmitSuccess(result?.message || 'Inventory deleted successfully.');
      resetForm();
    } catch (error) {
      setSubmitError(error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Grid container spacing={2.5} className='admin-dashboard-metrics'>
        {headerMetrics.map((metric) => (
          <Grid key={metric.label} size={{ xs: 12, md: 4 }}>
            <Paper className='admin-dashboard-metricCard' elevation={0}>
              <Typography className='admin-dashboard-metricLabel'>
                {metric.label}
              </Typography>
              <Typography className='admin-dashboard-metricValue'>
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, xl: 5 }}>
          <Paper className='admin-dashboard-featureCard admin-crud-formCard' elevation={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  {editingId ? 'Edit inventory' : 'Create inventory'}
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {editingId ? 'Update stock levels' : 'Add inventory item'}
                </Typography>
              </Box>
              <Chip
                label={editingId ? 'Edit mode' : 'Variant image selector'}
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-cardText'>
              Select the product first, then choose the variant from a visual
              picker that shows the variant image and variant name while the
              backend still receives only `variant_id`.
            </Typography>

            {submitError ? (
              <Alert severity='error' className='auth-alert'>
                {submitError.message}
              </Alert>
            ) : null}

            {submitSuccess ? (
              <Alert severity='success' className='auth-alert'>
                {submitSuccess}
              </Alert>
            ) : null}

            <Box component='form' onSubmit={handleSubmit} className='admin-crud-form'>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={products}
                    value={selectedProduct}
                    onChange={handleProductChange}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) =>
                      String(option.product_id) === String(value.product_id)
                    }
                    renderOption={(props, option) => (
                      <Box component='li' {...props}>
                        <Avatar
                          src={buildProductImageUrl(option.image)}
                          alt={option.name}
                          sx={{ width: 40, height: 40, mr: 1.5 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <Typography variant='body2' sx={{ color: '#7a5a49' }}>
                            Product ID: {option.product_id}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Search product'
                        placeholder='Search by product name'
                        error={Boolean(fieldErrors.product_id)}
                        helperText={fieldErrors.product_id}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={variants}
                    value={selectedVariant}
                    onChange={handleVariantChange}
                    loading={isVariantLoading}
                    disabled={!formValues.product_id}
                    getOptionLabel={(option) => option.variant_name || ''}
                    isOptionEqualToValue={(option, value) =>
                      String(option.variant_id) === String(value.variant_id)
                    }
                    renderOption={(props, option) => (
                      <Box component='li' {...props}>
                        <Avatar
                          src={buildVariantImageUrl(option.image)}
                          alt={option.variant_name}
                          sx={{ width: 40, height: 40, mr: 1.5 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {option.variant_name}
                          </Typography>
                          <Typography variant='body2' sx={{ color: '#7a5a49' }}>
                            SKU: {option.sku || '-'} | {option.weight || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Search variant'
                        placeholder='Search by variant name'
                        error={Boolean(fieldErrors.variant_id)}
                        helperText={
                          fieldErrors.variant_id ||
                          (!formValues.product_id
                            ? 'Select a product to load variants.'
                            : '')
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Stock'
                    value={formValues.stock}
                    onChange={handleFieldChange('stock')}
                    error={Boolean(fieldErrors.stock)}
                    helperText={fieldErrors.stock}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Reserved stock'
                    value={formValues.reserved_stock}
                    onChange={handleFieldChange('reserved_stock')}
                    disabled={!editingId}
                    error={Boolean(fieldErrors.reserved_stock)}
                    helperText={
                      fieldErrors.reserved_stock ||
                      (!editingId ? 'Reserved stock becomes editable after load.' : '')
                    }
                  />
                </Grid>
              </Grid>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                className='admin-crud-actions'
              >
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={editingId ? <SaveRoundedIcon /> : <AddRoundedIcon />}
                  className='admin-crud-primaryButton'
                  disabled={isSaving}
                >
                  {isSaving
                    ? editingId
                      ? 'Updating...'
                      : 'Creating...'
                    : editingId
                      ? 'Update inventory'
                      : 'Add inventory'}
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={resetForm}
                  className='admin-crud-secondaryButton'
                >
                  Reset
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, xl: 7 }}>
          <Paper className='admin-dashboard-sideCard admin-crud-tableCard' elevation={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  Live inventory
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {section.label} details
                </Typography>
              </Box>
              <Chip
                label={
                  !formValues.variant_id
                    ? 'Select variant'
                    : isLoading
                      ? 'Loading...'
                      : inventoryRows.length
                        ? '1 record'
                        : 'No record'
                }
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-note'>
              The variant selection now includes the variant name and variant
              image for faster stock management. Inventory APIs still receive
              only the selected `variant_id`.
            </Typography>
            <Divider className='admin-dashboard-divider' />

            <TableContainer className='admin-crud-tableWrap'>
              <Table className='admin-crud-table'>
                <TableHead>
                  <TableRow>
                    {visibleColumns.map((column) => (
                      <TableCell key={column.key}>{column.label}</TableCell>
                    ))}
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 1}>
                        <Typography className='admin-dashboard-note'>
                          Loading inventory...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!isLoading &&
                    inventoryRows.map((item) => (
                      <TableRow key={item.inventory_id} hover>
                        {visibleColumns.map((column) => (
                          <TableCell key={column.key}>{item[column.key] || '-'}</TableCell>
                        ))}
                        <TableCell align='right'>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent='flex-end'
                            className='admin-crud-rowActions'
                          >
                            <Button
                              size='small'
                              variant='text'
                              startIcon={<EditRoundedIcon />}
                              onClick={() => handleEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size='small'
                              color='error'
                              variant='text'
                              startIcon={<DeleteOutlineRoundedIcon />}
                              onClick={() => handleDelete(item)}
                              disabled={deletingId === item.inventory_id}
                            >
                              {deletingId === item.inventory_id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                  {!isLoading && !formValues.variant_id ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 1}>
                        <Typography className='admin-dashboard-note'>
                          Select a variant to load inventory details.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!isLoading && formValues.variant_id && inventoryRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 1}>
                        <Typography className='admin-dashboard-note'>
                          No inventory record exists for this variant yet. You can
                          create one from the form.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            {selectedVariant?.image ? (
              <Box
                component='a'
                href={buildVariantImageUrl(selectedVariant.image)}
                target='_blank'
                rel='noreferrer'
                sx={{
                  mt: 2,
                  display: 'inline-flex',
                  color: '#8d4314',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                View selected variant image
              </Box>
            ) : null}
          </Paper>
        </Grid>
      </Grid>

      {section.stats?.length ? (
        <Box className='admin-dashboard-pillGrid'>
          {section.stats.map((item) => (
            <Box key={item} className='admin-dashboard-pill'>
              {item}
            </Box>
          ))}
        </Box>
      ) : null}
    </>
  );
}
